import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { environment } from '../../../environments/environment';
import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class FcmService {
  private messaging: Messaging | null = null;
  private app: FirebaseApp | null = null;
  private activeChatId: string | null = null;

  constructor(private http: HttpClient) {}

  /** Call from chat component when a conversation is opened/closed */
  setActiveChatId(id: string | null): void {
    this.activeChatId = id;
  }

  async init(): Promise<void> {
    const user = StorageService.getItem('self');
    if (!user || !['EMPLOYEE', 'ADMIN'].includes(user.user_type)) return;

    try {
      if (!('serviceWorker' in navigator) || !('Notification' in window)) return;

      // Register the Firebase messaging service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/',
      });

      // Initialize Firebase app once
      this.app = getApps().length
        ? getApps()[0]
        : initializeApp(environment.firebase);

      this.messaging = getMessaging(this.app);

      // Listen for foreground messages — suppress if user already has that chat open
      onMessage(this.messaging, (payload) => {
        const incomingChatId = payload.data?.['chat_id'];
        if (incomingChatId && incomingChatId === this.activeChatId) return;
        this.showForegroundNotification(payload);
      });

      if (Notification.permission === 'granted') {
        await this.getAndRegisterToken(registration);
      } else if (Notification.permission === 'default') {
        // Safari on macOS (16.4+) silently ignores Notification.requestPermission()
        // unless it's called synchronously from inside a real user gesture (click/
        // tap/keydown) — this init() runs from a socket 'connect' event, which is
        // several async hops away from any click, so the prompt never appeared and
        // nothing failed loudly. Chrome/Firefox tolerate the async call, so this
        // works everywhere, not just as a Safari-specific branch.
        this.requestPermissionOnNextGesture(registration);
      }
      // 'denied' -- nothing to do, the browser won't re-prompt until the user
      // resets the site's permission themselves.
    } catch (_) {
    }
  }

  private requestPermissionOnNextGesture(registration: ServiceWorkerRegistration): void {
    const handler = () => {
      document.removeEventListener('click', handler);
      document.removeEventListener('keydown', handler);
      document.removeEventListener('touchstart', handler);
      if (Notification.permission !== 'default') return;
      // Must be the first thing this handler does (not awaited, not behind
      // another async call) so the browser still considers it gesture-triggered.
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') this.getAndRegisterToken(registration);
      });
    };
    document.addEventListener('click', handler, { once: true });
    document.addEventListener('keydown', handler, { once: true });
    document.addEventListener('touchstart', handler, { once: true, passive: true });
  }

  private async getAndRegisterToken(registration: ServiceWorkerRegistration): Promise<void> {
    if (!this.messaging) return;
    try {
      const token = await getToken(this.messaging, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: registration,
      });
      if (!token) return;
      const user = StorageService.getItem('self');
      if (!user) return;
      await this.registerToken(user, token);
    } catch (_) {
    }
  }

  private registerToken(user: any, token: string): Promise<any> {
    const deviceId = this.getDeviceId();
    const body = {
      userId: user.id || user.user_id,
      fcmToken: token,
      platform: 'web',
      deviceId,
      projectId: environment.chat_project_id || null,
    };
    return this.http
      .post(`${environment.chat_socket_url}/fcm-token/register`, body)
      .toPromise()
      .catch(() => {});
  }

  private async showForegroundNotification(payload: any): Promise<void> {
    const title = payload.data?.['title'] || payload.notification?.title || 'New Message';
    const body  = payload.data?.['body']  || payload.notification?.body  || '';
    const icon  = payload.data?.['icon'];
    const chatType = payload.data?.['chat_type'];
    const senderId = payload.data?.['sender_id'];
    await this.showNotification(title, body, payload.data?.['chat_id'], icon, chatType, senderId);
  }

  /** Same native-OS notification FCM shows, usable for socket-delivered
   * notifications too (online recipient, chat not open -- see chat.component's
   * handleChatNotification), so both delivery paths look identical to the user.
   * icon: WhatsApp-style -- sender's photo for a 1:1 chat, group's photo for
   * a group message; falls back to the app logo if neither is set.
   * chatType/senderId: so a clicked notification can open the exact Direct/
   * Group/General view (see firebase-messaging-sw.js's notificationclick). */
  async showNotification(title: string, body: string, chatId?: string, icon?: string, chatType?: string, senderId?: string): Promise<void> {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const resolvedIcon = icon || '/assets/img/logo.png';

    // Use the service worker registration to show the notification.
    // This avoids Chrome prepending "localhost:4200" to the body (which happens
    // with new Notification() from page context but NOT from a SW registration).
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      registration.showNotification(title, {
        body,
        icon: resolvedIcon,
        tag: chatId || 'chat',
        data: { chat_id: chatId, chat_type: chatType, sender_id: senderId },
        requireInteraction: false,
      });
    } else {
      const n = new Notification(title, { body, icon: resolvedIcon });
      n.onclick = () => { window.focus(); n.close(); };
    }
  }

  private getDeviceId(): string {
    const key = 'fcm_device_id';
    let id = localStorage.getItem(key);
    if (!id) {
      id = `web_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      localStorage.setItem(key, id);
    }
    return id;
  }

  async removeToken(): Promise<void> {
    try {
      const user = StorageService.getItem('self');
      if (!user) return;
      const deviceId = this.getDeviceId();
      await this.http
        .post(`${environment.chat_socket_url}/fcm-token/remove`, {
          userId: user.id || user.user_id,
          deviceId,
        })
        .toPromise();
    } catch (_) {
    }
  }
}
